import { useCallback } from "react";
import { UseFormGetValues } from "react-hook-form";
import { updateProviderState } from "services/paymentApi";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../../../../utils/errorHandler";
import type { ConfigMode } from "../Context/types";
import { ExistingProvider, FormData } from "../types";

interface UseProviderStatusProps {
  mode: ConfigMode;
  dealerId: string;
  existingProvider: ExistingProvider | null;
  getValues: UseFormGetValues<FormData>;
  setExistingProvider: (provider: ExistingProvider) => void;
  onProviderStatusChanged?: (providerId?: string, newState?: string) => void;
}

export function useProviderStatus({
  mode,
  dealerId,
  existingProvider,
  getValues,
  setExistingProvider,
  onProviderStatusChanged,
}: UseProviderStatusProps) {
  const handleStatusChange = useCallback(
    async (isActive: boolean) => {
      if (mode !== "EDIT" || !existingProvider || !dealerId) {
        return;
      }

      try {
        const state = isActive ? "enabled" : "disabled";
        await updateProviderState({
          merchantId: dealerId,
          merchantProviderId: existingProvider.id,
          state,
        });

        const formData = getValues();
        const updatedProvider: ExistingProvider = {
          id: existingProvider.id,
          priority: formData.priority ?? existingProvider.priority,
          merchantId: formData.merchantId ?? existingProvider.merchantId,
          mcc: formData.mcc?.toString() ?? existingProvider.mcc,
          isActive,
          merchantKey: formData.merchantKey ?? existingProvider.merchantKey,
          brands: formData.brands?.length
            ? formData.brands.map((brand: string) => ({
                id: brand,
                brand,
                merchantId: formData.merchantId ?? existingProvider.merchantId,
              }))
            : existingProvider.brands,
          credentials: formData.credentials ?? existingProvider.credentials,
          provider: existingProvider.provider,
        };
        setExistingProvider(updatedProvider);

        toast("Status atualizado com sucesso!", { type: "success" });

        const stateValue = isActive ? "Enabled" : "Disabled";
        onProviderStatusChanged?.(existingProvider.id, stateValue);
      } catch (error: unknown) {
        const errorMessage = getErrorMessage(
          error,
          "Não foi possivel inativar. Tente novamente!",
        );
        toast(errorMessage, { type: "error" });
      }
    },
    [
      mode,
      existingProvider,
      dealerId,
      setExistingProvider,
      getValues,
      onProviderStatusChanged,
    ],
  );

  return {
    handleStatusChange,
  };
}
